<template>
  <BDLayout>
    <div class="transaction">
      <section id="bd-opportunity-detail">
        <div class="container">
         

          <h6 class="fw-bold mb-1">{{ opportunity.title || 'Opportunity Detail' }}</h6>
          <p class="text-muted mb-3">
            {{ opportunity.tenderRefNo || opportunity.referenceNo }} · {{ opportunity.client }} ·
            <span>Compliance: <strong :class="complianceClass">{{ complianceLabel }}</strong></span>
          </p>

          <!-- Stepper for Modules 2-5 -->
          <div class="bd-stepper mb-3">
            <div
              v-for="step in steps"
              :key="step.key"
              class="bd-step"
              :class="{ active: currentTab === step.key, done: stepDone(step.key) }"
              @click="currentTab = step.key"
            >
              <div class="circle">{{ step.number }}</div>
              <div class="label">
                {{ step.label }}
                <span v-if="step.key === 'decision' && opportunity.status !== 'APPROVED'" class="badge bg-danger ms-2">Bid not yet approved</span>
              </div>
            </div>
          </div>

          <div class="card mb-3">
            <div class="card-body">
              <!-- Tabs content -->
              <transition name="fade-slide" mode="out-in">
                <div v-if="currentTab === 'opportunity'">
                <h6 class="fw-bold mb-2">Stage A – Opportunity Identified</h6>
                <form @submit.prevent="saveOpportunity">
                  <div class="row">
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Reference No</label>
                        <input v-model="form.tenderRefNo" type="text" class="form-control" :disabled="!editingOpportunity" />
                      </div>
                    </div>
                    <div class="col-md-8">
                      <div class="form-group mb-2">
                        <label>Title</label>
                        <input v-model="form.title" type="text" class="form-control" :disabled="!editingOpportunity" />
                      </div>
                    </div>

                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Client</label>
                        <input v-model="form.client" type="text" class="form-control" :disabled="!editingOpportunity" />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Country</label>
                        <input v-model="form.country" type="text" class="form-control" :disabled="!editingOpportunity" />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Department</label>
                        <select v-model="form.department" class="form-control" :disabled="!editingOpportunity">
                          <option value="">Select Department</option>
                          <option v-for="d in departments" :key="d.id" :value="d.name">
                            {{ d.name }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Sector</label>
                        <input v-model="form.sector" type="text" class="form-control" :disabled="!editingOpportunity" />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Tender Type</label>
                        <select v-model="form.tenderTypeId" class="form-control" :disabled="!editingOpportunity">
                          <option value="">Select Tender Type</option>
                          <option v-for="t in tenderTypes" :key="t.id" :value="t.id">
                            {{ t.name }}
                          </option>
                        </select>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Estimated Value</label>
                        <input v-model.number="form.estimatedValue" type="number" class="form-control" :disabled="!editingOpportunity" />
                      </div>
                    </div>

                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Pre-Bid Meeting</label>
                        <input v-model="form.preBidMeeting" type="date" class="form-control" :disabled="!editingOpportunity" />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Site Visit</label>
                        <input v-model="form.siteVisit" type="date" class="form-control" :disabled="!editingOpportunity" />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Clarification Deadline</label>
                        <input v-model="form.clarificationDeadline" type="date" class="form-control" :disabled="!editingOpportunity" />
                      </div>
                    </div>

                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Source</label>
                        <input v-model="form.source" type="text" class="form-control" :disabled="!editingOpportunity" />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Submission Deadline</label>
                        <input v-model="form.submissionDeadline" type="datetime-local" class="form-control" :disabled="!editingOpportunity" />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Bid Security Required</label>
                        <select v-model="form.bidSecurityRequired" class="form-control" :disabled="!editingOpportunity">
                          <option :value="true">Yes</option>
                          <option :value="false">No</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end mt-2">
                    <button class="btn btn-primary" type="button" @click="toggleOpportunityEdit" :disabled="saving">
                      <span v-if="editingOpportunity && !saving">Save</span>
                      <span v-if="editingOpportunity && saving">Saving...</span>
                      <span v-if="!editingOpportunity">Edit</span>
                    </button>
                  </div>
                </form>
                </div>

                <div v-else-if="currentTab === 'qualification'">
                <h6 class="fw-bold mb-2">Stage B – Qualification Review</h6>
                <form @submit.prevent="saveQualification">
                  <div class="row">
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Capacity Availability</label>
                        <select v-model="qualification.capacity" class="form-control">
                          <option value="ADEQUATE">Adequate</option>
                          <option value="INSUFFICIENT">Insufficient</option>
                        </select>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Risk Level</label>
                        <select v-model="qualification.riskLevel" class="form-control">
                          <option value="LOW">Low</option>
                          <option value="MEDIUM">Medium</option>
                          <option value="HIGH">High</option>
                        </select>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Budget Feasibility</label>
                        <select v-model="qualification.budgetFeasibility" class="form-control">
                          <option value="FEASIBLE">Feasible</option>
                          <option value="TIGHT">Tight</option>
                          <option value="NOT_FEASIBLE">Not Feasible</option>
                        </select>
                      </div>
                    </div>

                    <div class="col-md-12">
                      <div class="form-group mb-2">
                        <label>Remarks</label>
                        <textarea v-model="qualification.remarks" class="form-control" rows="3" :disabled="!editingQualification" />
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end mt-2">
                    <button class="btn btn-primary" type="button" @click="toggleQualificationEdit" :disabled="savingQualification">
                      <span v-if="editingQualification && !savingQualification">Save Qualification</span>
                      <span v-if="editingQualification && savingQualification">Saving...</span>
                      <span v-if="!editingQualification">Edit</span>
                    </button>
                  </div>
                </form>
              </div>

                <div v-else-if="currentTab === 'decision'">
                <h6 class="fw-bold mb-2">Stage C – Go / No-Go Approval</h6>
                <form @submit.prevent="saveDecision">
                  <div class="row">
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Decision</label>
                        <select v-model="decision.status" class="form-control" :disabled="!editingDecision || !canEditDecision">
                          <option value="UNDER_REVIEW">Under Review</option>
                          <option value="APPROVED">Go (Approved)</option>
                          <option value="REJECTED">No-Go (Rejected)</option>
                        </select>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Approved / Rejected By</label>
                        <select v-model="decision.approvedById" class="form-control" :disabled="!editingDecision || !canEditDecision">
                          <option value="">Select User</option>
                          <option
                            v-for="u in users"
                            :key="u.id"
                            :value="u.id"
                          >
                            {{ u.name || u.email || ('User #' + u.id) }}
                          </option>
                        </select>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Decision Date</label>
                        <input v-model="decision.decisionDate" type="date" class="form-control" :disabled="!editingDecision || !canEditDecision" />
                      </div>
                    </div>

                    <div class="col-md-12">
                      <div class="form-group mb-2">
                        <label>Approval Notes</label>
                        <textarea v-model="decision.remarks" class="form-control" rows="3" :disabled="!editingDecision || !canEditDecision" />
                      </div>
                    </div>
                  </div>

                  <!-- Decision editing is not allowed inline: hide save/edit controls here -->
                </form>

                <p class="text-muted small mb-0 text-danger" style="color: orange !important;">
                  When Approved: deadline countdown and pipeline value contribute to dashboards.
                </p>
              </div>

                <div v-else-if="currentTab === 'preparation'">
                  <h6 class="fw-bold mb-2">Bid Preparation
                    <span v-if="opportunity.status !== 'APPROVED'" class="badge bg-danger ms-2">Bid not yet approved</span>
                  </h6>
                <form @submit.prevent="savePreparation">
                  <div class="row">
                    <div class="col-md-3">
                      <div class="form-group mb-2">
                        <label>Status</label>
                        <select v-model="preparation.status" class="form-control" :disabled="!editingPreparation">
                          <option value="NOT_STARTED">Not Started</option>
                          <option value="IN_PROGRESS">In Progress</option>
                          <option value="COMPLETED">Completed</option>
                          <option value="SUBMITTED">Submitted</option>
                        </select>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="form-group mb-2">
                        <label>Assigned To</label>
                        <select v-model="preparation.ownerId" class="form-control" :disabled="!editingPreparation">
                          <option value="">Unassigned</option>
                          <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name || u.email }}</option>
                        </select>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="form-group mb-2">
                        <label>Pre-Bid Meeting Held</label>
                        <select v-model="preparation.preBidHeld" class="form-control" :disabled="!editingPreparation">
                          <option :value="true">Yes</option>
                          <option :value="false">No</option>
                        </select>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="form-group mb-2">
                        <label>Site Visit Done</label>
                        <select v-model="preparation.siteVisitDone" class="form-control" :disabled="!editingPreparation">
                          <option :value="true">Yes</option>
                          <option :value="false">No</option>
                        </select>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="form-group mb-2">
                        <label>Clarifications Sent</label>
                        <select v-model="preparation.clarificationsSent" class="form-control" :disabled="!editingPreparation">
                          <option :value="true">Yes</option>
                          <option :value="false">No</option>
                        </select>
                      </div>
                    </div>

                    <div class="col-md-12">
                      <div class="form-group mb-2">
                        <label>Key Activities / Notes</label>
                        <textarea v-model="preparation.notes" class="form-control" :disabled="!editingPreparation" style="height:120px; max-height:320px; min-height:60px; resize:vertical;"></textarea>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end mt-2" v-if="opportunity.status === 'APPROVED'">
                    <button class="btn btn-primary" type="button" @click="togglePreparationEdit" :disabled="savingPreparation">
                      <span v-if="editingPreparation && !savingPreparation">Save Preparation</span>
                      <span v-if="editingPreparation && savingPreparation">Saving...</span>
                      <span v-if="!editingPreparation">Edit</span>
                    </button>
                  </div>
                </form>
                <div class="mt-3">
                  <bid-checklist :preparation-id="preparation.id" :opportunity-id="opportunity.id" v-if="opportunity.id"></bid-checklist>
                </div>
              </div>

                <div v-else-if="currentTab === 'submission'">
                <h6 class="fw-bold mb-2">Module 4 – Submission Tracking</h6>
                <form @submit.prevent="saveSubmission">
                  <div class="row">
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Submission Method</label>
                        <input v-model="submission.method" type="text" class="form-control" :disabled="!editingSubmission" />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Bid Amount</label>
                        <input v-model.number="submission.bidAmount" type="number" class="form-control" />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>% Margin</label>
                        <input v-model.number="submission.margin" type="number" class="form-control" step="0.01" />
                      </div>
                    </div>

                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Bid Preparation Cost</label>
                        <input v-model.number="submission.preparationCost" type="number" class="form-control" />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Submission Date &amp; Time</label>
                        <input v-model="submission.submissionDate" type="datetime-local" class="form-control" />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Submitted By</label>
                        <select v-model="submission.submittedById" class="form-control" :disabled="!editingSubmission">
                          <option value="">Select User</option>
                          <option
                            v-for="u in users"
                            :key="u.id"
                            :value="u.id"
                          >
                            {{ u.name || u.email || ('User #' + u.id) }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Bid Validity Expiry</label>
                        <input v-model="submission.validityExpiry" type="date" class="form-control" />
                      </div>
                    </div>
                    <div class="col-md-8">
                      <div class="form-group mb-2">
                        <label>Proof (URL / Ref)</label>
                        <input v-model="submission.proof" type="text" class="form-control" />
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end mt-2">
                    <button class="btn btn-primary" type="button" @click="toggleSubmissionEdit" :disabled="savingSubmission">
                      <span v-if="editingSubmission && !savingSubmission">Save Submission</span>
                      <span v-if="editingSubmission && savingSubmission">Saving...</span>
                      <span v-if="!editingSubmission">Edit</span>
                    </button>
                  </div>
                </form>
              </div>

                <div v-else-if="currentTab === 'award'">
                <h6 class="fw-bold mb-2">Award &amp; Contract Management</h6>
                <form @submit.prevent="saveAward">
                  <div class="row">
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Outcome Status</label>
                        <select v-model="award.status" class="form-control">
                          <option value="UNDER_EVALUATION">Under Evaluation</option>
                          <option value="AWARDED">Awarded</option>
                          <option value="LOST">Lost</option>
                        </select>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Award Date</label>
                        <input v-model="award.awardDate" type="date" class="form-control" :disabled="!editingAward" />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Contract Value</label>
                        <input v-model.number="award.contractValue" type="number" class="form-control" />
                      </div>
                    </div>

                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Contract Duration (months)</label>
                        <input v-model.number="award.contractDuration" type="number" class="form-control" />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Performance Bond</label>
                        <input v-model="award.performanceBond" type="text" class="form-control" />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Advance Payment Guarantee</label>
                        <input v-model="award.advancePaymentGuarantee" type="text" class="form-control" />
                      </div>
                    </div>

                    <div class="col-md-4">
                      <div class="form-group mb-2">
                        <label>Project Owner</label>
                        <select v-model="award.projectOwnerId" class="form-control" :disabled="!editingAward">
                          <option value="">Select User</option>
                          <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name || u.email || ('User #' + u.id) }}</option>
                        </select>
                      </div>
                    </div>

                    <div class="col-md-12" v-if="award.status === 'LOST'">
                      <div class="form-group mb-2">
                        <label>Loss Reason</label>
                        <textarea v-model="award.lossReason" class="form-control" rows="2" />
                      </div>
                    </div>
                    <div class="col-md-12" v-if="award.status === 'LOST'">
                      <div class="form-group mb-2">
                        <label>Competitor (optional)</label>
                        <input v-model="award.competitor" type="text" class="form-control" />
                      </div>
                    </div>
                    <div class="col-md-12" v-if="award.status === 'LOST'">
                      <div class="form-group mb-2">
                        <label>Lessons Learned</label>
                        <textarea v-model="award.lessonsLearned" class="form-control" rows="3" />
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end mt-2">
                    <button class="btn btn-primary" type="button" @click="toggleAwardEdit" :disabled="savingAward">
                      <span v-if="editingAward && !savingAward">Save Award / Outcome</span>
                      <span v-if="editingAward && savingAward">Saving...</span>
                      <span v-if="!editingAward">Edit</span>
                    </button>
                  </div>
                </form>

                <p class="text-muted small mb-0 mt-2">
                  When Awarded: status flows to Active Contract &amp; Handover to Projects.
                </p>
              </div>
            </transition>
            </div>
          </div>
        </div>
      </section>
    </div>
  </BDLayout>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import BDLayout from '@/components/BDLayout.vue'
import BidChecklist from '@/components/BidChecklist.vue'
import { bidApi } from '@/api/bid'
// tender types will be loaded from the API at runtime

export default {
  name: 'BidOpportunityDetail',
  components: { BDLayout, BidChecklist },
  setup() {
    const route = useRoute()
    const store = useStore()
    const id = route.params.id

    const loading = ref(false)
    const opportunity = reactive({})
    const currentTab = ref('opportunity')

    const form = reactive({
      tenderTypeId: '',
    })
    const tenderTypes = ref([])
    const qualification = reactive({
      capacity: 'ADEQUATE',
      riskLevel: 'MEDIUM',
      budgetFeasibility: 'FEASIBLE',
      remarks: '',
    })
    const decision = reactive({
      status: 'UNDER_REVIEW',
      approvedById: '',
      decisionDate: '',
      remarks: '',
    })
    const preparation = reactive({
      status: 'NOT_STARTED',
      preBidHeld: false,
      siteVisitDone: false,
      clarificationsSent: false,
      notes: '',
    })
    const submission = reactive({
      method: '',
      bidAmount: null,
      margin: null,
      preparationCost: null,
      submissionDate: '',
      submittedById: '',
      proof: '',
      validityExpiry: '',
    })
    const award = reactive({
      status: 'UNDER_EVALUATION',
      awardDate: '',
      contractValue: null,
      contractDuration: null,
      performanceBond: '',
      advancePaymentGuarantee: '',
      projectOwner: '',
      lossReason: '',
      competitor: '',
      lessonsLearned: '',
    })

    const saving = ref(false)
    const savingQualification = ref(false)
    const savingDecision = ref(false)
    const savingPreparation = ref(false)
    const savingSubmission = ref(false)
    const savingAward = ref(false)

    // editing flags for forms: when false inputs are disabled and button shows Edit
    const isExisting = !!(id && id !== 'new')
    // for existing opportunities default to read-only (show "Edit")
    const editingOpportunity = ref(!isExisting)
    const editingQualification = ref(!isExisting)
    const editingDecision = ref(!isExisting)
    const editingPreparation = ref(!isExisting)
    const editingSubmission = ref(!isExisting)
    const editingAward = ref(!isExisting)

    const departments = computed(() => store.getters.getDepartments || [])
    const users = computed(() => store.getters.getUsers || [])

    // read permissions saved in localStorage (populated by RoleEdit)
    const rawPermission = localStorage.getItem('permission')
    const permission = ref(rawPermission ? JSON.parse(rawPermission) : {})

    const canApproveBidPermission = computed(() => !!permission.value.canApproveBidOpportunity)
    const allowedBidDepartments = computed(() => permission.value.bidOpportunityDepartments || [])

    const isDeptAllowedForApproval = computed(() => {
      if (!allowedBidDepartments.value || allowedBidDepartments.value.length === 0) return true
      const deptId = opportunity.departmentId || opportunity.department?.id || opportunity.department
      if (deptId == null) return false
      return allowedBidDepartments.value.map(String).includes(String(deptId))
    })

    const canEditDecision = computed(() => canApproveBidPermission.value && isDeptAllowedForApproval.value)

    const steps = [
      { key: 'opportunity', number: 1, label: 'Opportunity' },
      { key: 'qualification', number: 2, label: 'Qualification Review' },
      { key: 'decision', number: 3, label: 'Go / No-Go' },
      { key: 'preparation', number: 4, label: 'Bid Preparation' },
      { key: 'submission', number: 5, label: 'Submission' },
      { key: 'award', number: 6, label: 'Award & Contract' },
    ]

    const complianceLabel = computed(() => {
      const health = opportunity.complianceHealth || opportunity.complianceStatus
      if (!health) return 'Unknown'
      if (health === 'OK' || health === 'GOOD') return 'Healthy'
      if (health === 'WARNING') return 'Check Documents'
      if (health === 'CRITICAL' || health === 'EXPIRED') return 'Non-Compliant'
      return health
    })

    const complianceClass = computed(() => {
      const health = opportunity.complianceHealth || opportunity.complianceStatus
      switch (health) {
        case 'OK':
        case 'GOOD':
          return 'text-success'
        case 'WARNING':
          return 'text-warning'
        case 'CRITICAL':
        case 'EXPIRED':
          return 'text-danger'
        default:
          return 'text-muted'
      }
    })

    const stepDone = (key) => {
      if (key === 'opportunity') return !!opportunity.id
      if (key === 'qualification') return !!opportunity.qualificationCompleted
      if (key === 'decision') return opportunity.status === 'APPROVED' || opportunity.status === 'REJECTED'
      if (key === 'preparation') return preparation.status === 'COMPLETED' || preparation.status === 'SUBMITTED'
      if (key === 'submission') return !!submission.submissionDate
      if (key === 'award') return award.status === 'AWARDED' || award.status === 'LOST'
      return false
    }

    const loadOpportunity = async () => {
      loading.value = true
      try {
        const data = await bidApi.getOpportunity(id)
        const op = data.data || data
        Object.assign(opportunity, op)

        Object.assign(form, {
          tenderRefNo: op.tenderRefNo,
          title: op.title,
          client: op.client,
          country: op.country,
          department: op.department,
          sector: op.sector,
          tenderType: op.tenderType,
          tenderTypeId: op.tenderTypeId || (op.tenderType?.id || ''),
          estimatedValue: op.estimatedValue,
          preBidMeeting: op.preBidMeeting,
          siteVisit: op.siteVisit,
          clarificationDeadline: op.clarificationDeadline,
          source: op.source,
          submissionDeadline: op.submissionDeadline,
          bidSecurityRequired: op.bidSecurityRequired,
        })

        // If we have a tenderTypeId but not the name, resolve it from loaded tenderTypes
        if (!form.tenderType && form.tenderTypeId && tenderTypes.value && tenderTypes.value.length) {
          const found = tenderTypes.value.find((t) => String(t.id) === String(form.tenderTypeId))
          if (found) form.tenderType = found.name
        }

        if (op.qualification) {
          // Map backend qualification shape to UI model
          const q = op.qualification
          // capacityAvailable (boolean) -> capacity string
          qualification.capacity = q.capacityAvailable === false ? 'INSUFFICIENT' : 'ADEQUATE'

          // riskAssessment may be stored as "LEVEL - remarks" or just a level
          if (q.riskAssessment && typeof q.riskAssessment === 'string') {
            const parts = q.riskAssessment.split(' - ')
            qualification.riskLevel = (parts[0] || 'MEDIUM').toUpperCase()
            qualification.remarks = parts.slice(1).join(' - ') || ''
          } else {
            qualification.riskLevel = 'MEDIUM'
            qualification.remarks = ''
          }

          // budgetFeasibility boolean -> UI enum
          qualification.budgetFeasibility = q.budgetFeasibility ? 'FEASIBLE' : 'NOT_FEASIBLE'
        }
        if (op.decision) Object.assign(decision, op.decision)
        if (op.bidPreparation) Object.assign(preparation, op.bidPreparation)
        if (op.award) Object.assign(award, op.award)

        // normalize date fields for date inputs
        if (op.preBidMeeting) form.preBidMeeting = String(op.preBidMeeting).slice(0, 10)
        if (op.siteVisit) form.siteVisit = String(op.siteVisit).slice(0, 10)
        if (op.clarificationDeadline) form.clarificationDeadline = String(op.clarificationDeadline).slice(0, 10)
        // submissionDeadline is a datetime-local input -> format as YYYY-MM-DDTHH:MM
        if (op.submissionDeadline) {
          const sd = String(op.submissionDeadline)
          form.submissionDeadline = sd.length >= 16 ? sd.slice(0, 16) : sd
        }

        // Pre-select approver from BidApproval, if present
        if (op.bidApproval) {
          decision.status = op.bidApproval.decision || decision.status
          decision.approvedById = op.bidApproval.approvedById?.toString() || decision.approvedById
          decision.decisionDate = op.bidApproval.approvalDate
            ? op.bidApproval.approvalDate.slice(0, 10)
            : decision.decisionDate
          decision.remarks = op.bidApproval.comments || decision.remarks
        }

        // Pre-select submitter from BidSubmission, if present
        if (op.bidSubmission) {
          submission.method = op.bidSubmission.submissionMethod || submission.method
          submission.bidAmount = op.bidSubmission.bidAmount ?? submission.bidAmount
          submission.margin = op.bidSubmission.marginPercentage ?? submission.margin
          submission.preparationCost =
            op.bidSubmission.bidPreparationCost ?? submission.preparationCost
          submission.submissionDate = op.bidSubmission.submissionDate
            ? op.bidSubmission.submissionDate.slice(0, 16)
            : submission.submissionDate
          submission.submittedById =
            (op.bidSubmission.submittedById != null
              ? op.bidSubmission.submittedById.toString()
              : submission.submittedById)
          submission.proof = op.bidSubmission.proofUrl || submission.proof
          submission.validityExpiry = op.bidSubmission.bidValidityExpiry
            ? op.bidSubmission.bidValidityExpiry.slice(0, 10)
            : submission.validityExpiry
        }
      } catch (err) {
        console.error('Failed to load opportunity', err)
      } finally {
        loading.value = false
      }
    }

    const loadTenderTypes = async () => {
      try {
        const base = (process.env.VUE_APP_URL || 'http://localhost:3000').replace(/\/+$/, '')
        const res = await fetch(base + '/tender-type', {
          headers: {
            Accept: 'application/json',
            ...(localStorage.getItem('access_token')
              ? { Authorization: `Bearer ${JSON.parse(localStorage.getItem('access_token'))}` }
              : {}),
          },
        })
        if (!res.ok) return
        const data = await res.json()
        const list = data && typeof data === 'object' && (data.data || data.items || data.rows)
          ? data.data || data.items || data.rows
          : data
        tenderTypes.value = Array.isArray(list) ? list : []
      } catch (err) {
        console.error('Failed to load tender types', err)
      }
    }

    const saveOpportunity = async () => {
      saving.value = true
      try {
        const payload = { ...form }
        // ensure tenderType name is sent when tenderTypeId is selected
        if (form.tenderTypeId && !form.tenderType) {
          const sel = tenderTypes.value.find((t) => String(t.id) === String(form.tenderTypeId))
          if (sel) payload.tenderType = sel.name
        }
        const res = await bidApi.updateOpportunity(id, payload)
        Object.assign(opportunity, res.data || res)
        // mark as saved (read-only)
        editingOpportunity.value = false
      } catch (err) {
        console.error('Failed to save opportunity', err)
      } finally {
        saving.value = false
      }
    }

    const saveQualification = async () => {
      savingQualification.value = true
      try {
        const res = await bidApi.updateQualification(id, { ...qualification })
        if (res && res.qualification) Object.assign(qualification, res.qualification)
        editingQualification.value = false
      } catch (err) {
        console.error('Failed to save qualification', err)
      } finally {
        savingQualification.value = false
      }
    }

    const saveDecision = async () => {
      savingDecision.value = true
      try {
        const res = await bidApi.updateDecision(id, { ...decision })
        Object.assign(opportunity, res.data || res)
      } catch (err) {
        console.error('Failed to save decision', err)
      } finally {
        savingDecision.value = false
      }
    }

    const savePreparation = async () => {
      savingPreparation.value = true
      try {
        const res = await bidApi.updatePreparation(id, { ...preparation })
        if (res && res.preparation) Object.assign(preparation, res.preparation)
        editingPreparation.value = false
      } catch (err) {
        console.error('Failed to save preparation', err)
      } finally {
        savingPreparation.value = false
      }
    }

    const saveSubmission = async () => {
      savingSubmission.value = true
      try {
        const res = await bidApi.updateSubmission(id, { ...submission })
        if (res && res.submission) Object.assign(submission, res.submission)
        editingSubmission.value = false
      } catch (err) {
        console.error('Failed to save submission', err)
      } finally {
        savingSubmission.value = false
      }
    }

    const saveAward = async () => {
      savingAward.value = true
      try {
        // Pass submission.id so the API can link the LossRecord when status is LOST
        const payload = {
          ...award,
          submissionId: submission.id || undefined,
        }
        const res = await bidApi.updateAward(id, payload)
        if (res && res.award) Object.assign(award, res.award)
        // Reflect the LOST status back on the opportunity object so stepDone works
        if (award.status === 'LOST') Object.assign(opportunity, { status: 'LOST' })
        editingAward.value = false
      } catch (err) {
        console.error('Failed to save award', err)
      } finally {
        savingAward.value = false
      }
    }

    // toggle handlers: if currently editing -> save, else enable editing
    const toggleOpportunityEdit = async () => {
      if (editingOpportunity.value) {
        await saveOpportunity()
      } else {
        editingOpportunity.value = true
      }
    }

    const toggleQualificationEdit = async () => {
      if (editingQualification.value) {
        await saveQualification()
      } else {
        editingQualification.value = true
      }
    }

    const toggleDecisionEdit = async () => {
      if (editingDecision.value) {
        await saveDecision()
      } else {
        editingDecision.value = true
      }
    }

    const togglePreparationEdit = async () => {
      if (editingPreparation.value) {
        await savePreparation()
      } else {
        editingPreparation.value = true
      }
    }

    const toggleSubmissionEdit = async () => {
      if (editingSubmission.value) {
        await saveSubmission()
      } else {
        editingSubmission.value = true
      }
    }

    const toggleAwardEdit = async () => {
      if (editingAward.value) {
        await saveAward()
      } else {
        editingAward.value = true
      }
    }

    onMounted(async () => {
      if (!departments.value || !departments.value.length) {
        store.dispatch('fetchDepartments')
      }
      if (!users.value || !users.value.length) {
        store.dispatch('fetchUsers')
      }
      await loadTenderTypes()
      await loadOpportunity()
    })

    return {
      loading,
      opportunity,
      steps,
      currentTab,
      form,
      qualification,
      decision,
      preparation,
      submission,
      award,
      saving,
      savingQualification,
      savingDecision,
      savingPreparation,
      savingSubmission,
      savingAward,
      departments,
      complianceLabel,
      complianceClass,
      stepDone,
      tenderTypes,
      users,
      canEditDecision,
      // editing flags exposed to template
      editingOpportunity,
      editingQualification,
      editingDecision,
      editingPreparation,
      editingSubmission,
      editingAward,
      // toggle handlers exposed to template
      toggleOpportunityEdit,
      toggleQualificationEdit,
      toggleDecisionEdit,
      togglePreparationEdit,
      toggleSubmissionEdit,
      toggleAwardEdit,
      saveOpportunity,
      saveQualification,
      saveDecision,
      savePreparation,
      saveSubmission,
      saveAward,
    }
  },
}
</script>

<style scoped>
.bd-stepper {
  display: flex;
  gap: 11px;
  overflow-x: auto;
}

.bd-step {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid #dee2e6;
  cursor: pointer;
  white-space: nowrap;
}

.bd-step .circle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  background: #f1f3f5;
}

/* Make primary buttons orange in this view */
.btn-primary {
  background-color: #211c3f !important;
  border-color: #211c3f !important;
  color: #fff !important;
}
.btn-primary:hover, .btn-primary:focus {
  background-color: #ff7a00 !important;
  border-color: #ff7a00 !important;
}

.bd-step .label {
  font-size: 11px;
}

.bd-step.active {
  background: #0d6efd;
  color: #fff;
  border-color: #0d6efd;
}

.bd-step.active .circle {
  background: #fff;
  color: #0d6efd;
}

.bd-step.done {
  border-style: solid;
  border-color: #198754;
}

/* subtle fade + slide transition when switching sections */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 220ms ease, transform 220ms ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
